import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  type UseModalProps,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  type ModalOrderDataFragment,
  useCreateOrderMutation,
  useModalCreateOrderBrandOptionsQuery,
  useModalCreateOrderProjectOptionsQuery,
  useUpdateOrderMutation,
} from "@src/__generated__/graphql";
import {
  DropZone,
  FormRow,
  InputDatePicker,
  type IOption,
  Select,
  TextField,
} from "@src/components/ui-kit";
import { onError } from "@src/utils/apollo";
import { MediaList, type MediaModel } from "@src/utils/components/dropzone";
import {
  fieldToInputProps,
  fieldToSelectProps,
} from "@src/utils/forms/inputHelpers";
import { orderBy } from "lodash";
import { observer } from "mobx-react-lite";
import { useMemo, useState } from "react";
import { Form } from "./Form";
import { useFillForm } from "./useFillForm";

type TEditOrder = Omit<
  ModalOrderDataFragment,
  "date_of_order" | "files" | "pdfFiles"
> & {
  date_of_order?: ModalOrderDataFragment["date_of_order"] | Date;
  files?: (NonNullable<ModalOrderDataFragment["files"]>[0] | MediaModel)[];
};

export interface ModalOrderProps extends UseModalProps {
  order?: TEditOrder;
  prefillFields?: {
    brandId?: string;
    projectIds?: string[];
  };
  options?: {
    brand: IOption[];
    project: (IOption & { brandId: string })[];
  };
  onSubmit?: (order: ModalOrderDataFragment) => void;
}

const ModalOrder = ({
  order,
  prefillFields,
  options,
  onSubmit,
  ...modalProps
}: ModalOrderProps) => {
  const [form] = useState(() => new Form());

  useFillForm({ order, prefillFields }, form, modalProps.isOpen);

  const brandOptionsRequest = useModalCreateOrderBrandOptionsQuery({
    skip: !!options || !modalProps.isOpen,
    ...onError(),
  });

  const projectOptionsRequest = useModalCreateOrderProjectOptionsQuery({
    variables: { brandId: form.brand_id.value },
    skip: !!options || !form.brand_id.value || !modalProps.isOpen,
    ...onError(),
  });

  const _brandOptions = useMemo(() => {
    if (brandOptionsRequest.data?.clientsSimpleMap) {
      const { clientsSimpleMap } = brandOptionsRequest.data;

      return orderBy(
        clientsSimpleMap.flatMap((i) =>
          i.brands.map((j) => ({ label: j.name, value: j.id })),
        ),
        ["label"],
        ["asc"],
      );
    }

    return [];
  }, [brandOptionsRequest.data]);

  const _projectOptions = useMemo(() => {
    if (projectOptionsRequest.data?.projectsSimpleMap) {
      const { projectsSimpleMap } = projectOptionsRequest.data;

      return orderBy(
        projectsSimpleMap.map((i) => ({ label: i.title, value: i.id })),
        ["label"],
        ["asc"],
      );
    }

    return [];
  }, [projectOptionsRequest.data]);

  const passedProjectOptions = useMemo(() => {
    if (!options?.project || !form.brand_id.value) {
      return undefined;
    }

    return options.project.filter((i) => i.brandId === form.brand_id.value);
  }, [options?.project, form.brand_id.value]);

  const [create, { loading: createLoading }] = useCreateOrderMutation({
    onCompleted(data) {
      onSubmit?.(data.createOrder);
      onClose();
    },
    ...onError(),
  });

  const [update, { loading: updateLoading }] = useUpdateOrderMutation({
    onCompleted(data) {
      onSubmit?.(data.updateOrder);
      onClose();
    },
    ...onError(),
  });

  const onClose = () => {
    modalProps.onClose();
    form.reset();
  };

  const onAction = async () => {
    const { hasError } = await form.validate();

    if (hasError) {
      return;
    }

    if (order) {
      update({ variables: { input: { id: order.id, ...form.serialize() } } });
    } else {
      create({ variables: { input: form.serialize() } });
    }
  };

  return (
    <Modal
      isOpen={modalProps.isOpen}
      onClose={onClose}
      size="2xl"
      trapFocus={false}
    >
      <ModalOverlay />

      <ModalContent>
        <ModalHeader>{order ? t`Edit order` : t`Add new order`}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormRow title={<Trans>PO</Trans>}>
            <TextField
              id="field-order-po"
              {...fieldToInputProps(form.purchase_order_no)}
              type="text"
            />
          </FormRow>

          <FormRow title={<Trans>Date of order</Trans>}>
            <InputDatePicker
              error={form.date_of_order.error}
              selected={form.date_of_order.$}
              onChange={(val) => {
                form.date_of_order.onChange(val?.start);
              }}
            />
          </FormRow>
          <FormRow title={<Trans>Brand</Trans>}>
            <Select
              asPortal
              id="field-order-brand"
              value={form.brand_id.value}
              onChange={(val) => {
                form.brand_id.onChange(val);
                form.project_ids.reset();
              }}
              error={form.brand_id.error}
              options={options?.brand || _brandOptions}
              placeholder={t`Brand`}
            />
          </FormRow>
          <FormRow title={<Trans>Projects</Trans>}>
            <Select
              asPortal
              isMulti
              isDisabled={!form.brand_id.value}
              placeholder={t`Select project/s`}
              {...fieldToSelectProps(
                form.project_ids,
                passedProjectOptions || _projectOptions,
              )}
            />
          </FormRow>
          <DropZone
            accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/docx, application/pdf, text/plain, application/msword, image/jpeg, image/png"
            allowMultiple
            onDrop={(files) => form.addFiles(files)}
          />

          <MediaList
            files={form.files.$}
            onDelete={(val) => form.removeFile(val)}
          />
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="grey" onClick={onClose} variant="outline">
            <Trans>Cancel</Trans>
          </Button>
          <Button
            ml={3}
            isLoading={createLoading || updateLoading}
            onClick={onAction}
          >
            {order ? t`Save changes` : t`Save document`}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default observer(ModalOrder);
